import { FC, useMemo } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import type { ChartBindingValue, CustomChartPoint } from '@/entities/dashboards';
import { EmptyState } from '@/shared/ui';
import {
  slicePaletteBgClass,
  slicePaletteBgClassByCode,
  slicePaletteClass,
  slicePaletteClassByCode,
} from '../../utils';
import { ChartCardShell } from '../ChartCardShell';
import type { ChartAppearance } from '../chartElements';
import { ChartLegend, type ChartLegendItem } from './ChartLegend';
import { PieSliceTooltip } from './PieSliceTooltip';
import { toPieSlices } from './pieSlices';

type Props = {
  title: string;
  points: CustomChartPoint[];
  /** Оформление диаграммы: цвета, заданные категориям поимённо */
  appearance?: ChartAppearance;
  /** Выбор доли наполняет связанную диаграмму; не задан — доли не выбираются */
  onSelectElement?: (value: ChartBindingValue) => void;
  /** Выбранная доля — прочие показаны приглушённо */
  selectedElement?: ChartBindingValue;
};

/**
 * Круговая диаграмма: доли категорий в целом.
 *
 * Круг занимает всю ширину диаграммы, а сколько в отдельной доле — показывает подсказка по
 * наведению: перечень категорий с величинами сбоку отнимал бы у круга половину места. Какая
 * категория каким цветом окрашена, названо легендой под кругом — цвет иначе остался бы
 * неназванным, а подсказка отвечает про одну долю за раз.
 *
 * Цвет доли пользователь задаёт поимённо; незаданные доли красятся набором по своему месту в
 * выборке — заданные цвета порядок остальных не сдвигают.
 *
 * Рост круга ограничен сверху: на широкой доле ширины он вышел бы за отведённую диаграмме
 * высоту и потребовал прокрутки на ровном месте.
 */
export const PieChartView: FC<Props> = ({ title, points, appearance, onSelectElement, selectedElement }) => {
  const slices = useMemo(() => toPieSlices(points), [points]);

  const colors = useMemo(
    () =>
      slices.map((slice, index) => {
        const named = appearance?.categoryColors?.[slice.label];
        return {
          fillClass: named ? slicePaletteClassByCode(named) : slicePaletteClass(index),
          bgClass: named ? slicePaletteBgClassByCode(named) : slicePaletteBgClass(index),
        };
      }),
    [slices, appearance],
  );

  const legendItems = useMemo(
    (): ChartLegendItem[] => slices.map((slice, index) => ({ colorClass: colors[index].bgClass, label: slice.label })),
    [slices, colors],
  );

  if (slices.length === 0) {
    return (
      <ChartCardShell title={title}>
        <EmptyState />
      </ChartCardShell>
    );
  }

  return (
    <ChartCardShell title={title}>
      <div className="flex flex-col">
        <div className="aspect-square max-h-96 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={slices}
                dataKey="value"
                nameKey="label"
                isAnimationActive={false}
                className={onSelectElement ? 'cursor-pointer' : undefined}
              >
                {slices.map((slice, index) => (
                  <Cell
                    key={slice.label}
                    className={colors[index].fillClass}
                    fillOpacity={selectedElement !== undefined && selectedElement !== slice.label ? 0.4 : 1}
                    // Выбор ставится на самой доле: так она знает свою категорию и без разбора того,
                    // что библиотека кладёт в событие диаграммы.
                    onClick={onSelectElement ? () => onSelectElement(slice.label) : undefined}
                  />
                ))}
              </Pie>
              <Tooltip content={<PieSliceTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <ChartLegend items={legendItems} />
      </div>
    </ChartCardShell>
  );
};
